/**
 * Payment methods management section for the portal Account page.
 *
 * Lists saved payment methods with delete capability and opens the
 * add form in a Sidebar panel (consistent with the Cart page flow).
 * Fetches data through portal-specific endpoints — no CRM services imported.
 */

import {
	faCreditCard,
	faPlus,
	faStar as faStarRegular,
	faTrashCan,
	faUsers as faUsersRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
	faStar as faStarSolid,
	faUsers as faUsersSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	formatPaymentMethodSharedByLabel,
	paymentTypeLabel,
} from '@supply-mover-2-0/core';
import type {
	PaymentMethodSharingDependentDto,
	PortalPaymentMethodDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import {
	CardBrandIcon,
	PaymentMethodStopSharingDialog,
	Tooltip,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { PortalSidebar } from '../components/PortalSidebar';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalPaymentService from '../services/portalPaymentService';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';

interface PaymentMethodsSectionProps {
	/** When true, auto-open the add sidebar on mount (e.g. from cart page link) */
	autoOpenAdd?: boolean;
	/**
	 * Hide the card's own "Payment Methods" title row (icon + heading +
	 * Add link). The dedicated Payment Methods PAGE already titles itself
	 * (MobilePageHeader / h1) and owns the Add button in that header row,
	 * so the in-card copy would be a duplicate there. Embedded contexts
	 * (Account page card) keep the default self-labelled header.
	 */
	hideHeader?: boolean;
	/**
	 * Controlled visibility for the add sidebar — lets a parent-owned Add
	 * button (the page header's) drive the same sidebar. When provided,
	 * `onAddOpenChange` must be too; internal triggers (empty-state Add)
	 * route through it. Omit both for self-managed state.
	 */
	addOpen?: boolean;
	onAddOpenChange?: (open: boolean) => void;
}

export const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = ({
	autoOpenAdd = false,
	hideHeader = false,
	addOpen,
	onAddOpenChange,
}) => {
	const { t } = useTranslation();
	const { token } = usePortalAuth();

	const [methods, setMethods] = useState<Array<PortalPaymentMethodDto>>([]);
	const [supportedTypes, setSupportedTypes] = useState<
		Array<SupportedPaymentMethodType>
	>([]);
	const [loading, setLoading] = useState(true);
	const [internalShowAdd, setInternalShowAdd] = useState(autoOpenAdd);
	const showAddSidebar = addOpen ?? internalShowAdd;
	const setShowAddSidebar = (open: boolean): void => {
		if (onAddOpenChange != null) {
			onAddOpenChange(open);
		} else {
			setInternalShowAdd(open);
		}
	};
	const [deletingId, setDeletingId] = useState<number | null>(null);
	const [settingPrimaryId, setSettingPrimaryId] = useState<number | null>(null);
	// PD-17009 — methods other accounts in the ownership group share with this
	// one. Read-only: they can be paid with, never managed from here.
	const [sharedWithAccount, setSharedWithAccount] = useState<
		Array<PortalPaymentMethodDto>
	>([]);
	// Whether the active account belongs to an ownership group. The share
	// control is offered only then: an account without a group has nobody to
	// share with.
	const [canShareWithOwnership, setCanShareWithOwnership] = useState(false);
	const [sharingId, setSharingId] = useState<number | null>(null);
	// Why the last change to a share switch was not saved. The row only
	// changes once the server has saved it, so without this a refused change
	// would look like a press that did nothing.
	const [sharingError, setSharingError] = useState<string | null>(null);
	// The shared method about to be un-shared or deleted, with what on other
	// accounts depends on it: `undefined` while that is being looked up,
	// `null` when the lookup failed.
	const [stopSharingPrompt, setStopSharingPrompt] = useState<{
		method: PortalPaymentMethodDto;
		action: 'stopSharing' | 'delete';
		dependents: Array<PaymentMethodSharingDependentDto> | null | undefined;
	} | null>(null);

	const fetchMethods = useCallback(async () => {
		if (token == null) {
			return;
		}

		try {
			const response = await portalPaymentService.getPaymentMethods(token);
			if (response.success) {
				setMethods(response.data);
				setSharedWithAccount(response.sharedWithAccount ?? []);
				setCanShareWithOwnership(response.canShareWithOwnership === true);
			}
		} catch (err) {
			console.error('Failed to fetch payment methods:', err);
		}
	}, [token]);

	const fetchSupportedTypes = useCallback(async () => {
		if (token == null) {
			return;
		}

		try {
			const response = await portalPaymentService.getSupportedTypes(token);
			if (response.success) {
				setSupportedTypes(response.data);
			}
		} catch (err) {
			console.error('Failed to fetch supported payment types:', err);
		}
	}, [token]);

	// Fetch methods and supported types on mount
	useEffect(() => {
		const load = async (): Promise<void> => {
			setLoading(true);
			await Promise.all([fetchMethods(), fetchSupportedTypes()]);
			setLoading(false);
		};
		void load();
	}, [fetchMethods, fetchSupportedTypes]);

	const handleDelete = async (methodId: number): Promise<void> => {
		if (token == null) {
			return;
		}

		setDeletingId(methodId);
		try {
			const result = await portalPaymentService.deletePaymentMethod(
				token,
				methodId,
			);
			if (result.success) {
				setMethods((prev) => prev.filter((m) => m.id !== methodId));
			}
		} catch (err) {
			console.error('Failed to delete payment method:', err);
		} finally {
			setDeletingId(null);
		}
	};

	/**
	 * PD-17009 — save the share switch for one of the account's own methods
	 * and reflect it on the row.
	 */
	const saveSharing = async (
		method: PortalPaymentMethodDto,
		isShared: boolean,
	): Promise<void> => {
		if (token == null) {
			return;
		}

		setSharingId(method.id);
		setSharingError(null);
		try {
			const result = await portalPaymentService.setPaymentMethodSharing(
				token,
				method.id,
				isShared,
			);
			if (result.success) {
				setMethods((prev) =>
					prev.map((m) =>
						m.id === method.id
							? { ...m, isSharedWithOwnership: isShared }
							: m,
					),
				);
			} else {
				setSharingError('Sharing could not be changed. Please try again.');
			}
		} catch (err) {
			console.error('Failed to update payment method sharing:', err);
			// The server says why in plain words (for example, that the
			// account is no longer in an ownership group).
			setSharingError(
				err instanceof Error && err.message !== ''
					? err.message
					: 'Sharing could not be changed. Please try again.',
			);
		} finally {
			setSharingId(null);
		}
	};

	/**
	 * PD-17009 — open the confirmation for taking a shared method away from
	 * the accounts it is shared with, and look up what depends on it. The
	 * confirm button waits for the answer; a failed lookup still lets the
	 * change go ahead, with a warning that it could not be checked.
	 */
	const promptStopSharing = (
		method: PortalPaymentMethodDto,
		action: 'stopSharing' | 'delete',
	): void => {
		setStopSharingPrompt({ method, action, dependents: undefined });

		if (token == null) {
			return;
		}

		void portalPaymentService
			.getPaymentMethodSharingDependents(token, method.id)
			.then((response) => (response.success ? response.data : null))
			.catch(() => null)
			.then((dependents) => {
				setStopSharingPrompt((current) =>
					current != null && current.method.id === method.id
						? { ...current, dependents }
						: current,
				);
			});
	};

	/**
	 * The share control was pressed on one of the account's own methods.
	 * Sharing saves straight away, for a card or a bank account alike;
	 * only stopping sharing asks first.
	 */
	const handleToggleSharing = (method: PortalPaymentMethodDto): void => {
		if (method.isSharedWithOwnership) {
			promptStopSharing(method, 'stopSharing');

			return;
		}

		void saveSharing(method, true);
	};

	const handleSetPrimary = async (methodId: number): Promise<void> => {
		if (token == null) {
			return;
		}

		setSettingPrimaryId(methodId);
		try {
			const result = await portalPaymentService.setPrimaryPaymentMethod(
				token,
				methodId,
			);
			if (result.success) {
				// Update local state: unset old primary, set new one
				setMethods((prev) =>
					prev.map((m) => ({
						...m,
						mPaymentIsPrimary: m.id === methodId,
					})),
				);
			}
		} catch (err) {
			console.error('Failed to set primary payment method:', err);
		} finally {
			setSettingPrimaryId(null);
		}
	};

	const handleAddSuccess = (): void => {
		setShowAddSidebar(false);
		void fetchMethods();
	};

	/**
	 * Format the display label for a saved payment method.
	 * e.g. "Visa ****1234", "Debit Card ****0008" or "ACH ****5678".
	 * PD-16381: a card with no network name is named by its funding ("Card"
	 * when unclassified), never by the stored 'Credit Card' type.
	 */
	const formatMethodLabel = (method: PortalPaymentMethodDto): string => {
		const subtype = method.mPaymentSubtype;
		const typeName = paymentTypeLabel(
			method.mPaymentType,
			method.mPaymentCardCategory,
		);
		const lastFour = method.mPaymentLastFour;
		const label = subtype ?? (typeName !== '' ? typeName : t('Payment Method'));
		const masked = lastFour != null ? ` ****${lastFour}` : '';
		return `${label}${masked}`;
	};

	const formatExpiration = (method: PortalPaymentMethodDto): string | null => {
		if (method.mPaymentExpirationDate == null) {
			return null;
		}
		const date = new Date(method.mPaymentExpirationDate);
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear().toString();
		return `${month}/${year}`;
	};

	if (loading) {
		return (
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				{!hideHeader && (
					<div className="flex items-center gap-2 mb-3">
						<FontAwesomeIcon icon={faCreditCard} className="text-brand-muted" />
						<h2 className="text-sm font-semibold text-default">
							{t('Payment Methods')}
						</h2>
					</div>
				)}
				<div className="flex items-center justify-center py-6">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-portal-accent" />
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
				{!hideHeader && (
					<div className="flex items-center justify-between mb-3">
						<div className="flex items-center gap-2">
							<FontAwesomeIcon
								icon={faCreditCard}
								className="text-brand-muted"
							/>
							<h2 className="text-sm font-semibold text-default">
								{t('Payment Methods')}
							</h2>
						</div>
						<button
							type="button"
							onClick={() => {
								setShowAddSidebar(true);
							}}
							className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:text-portal-accent"
						>
							<FontAwesomeIcon icon={faPlus} />
							<span>{t('Add')}</span>
						</button>
					</div>
				)}

				{/* Empty state (PD-14699) — icon + one-liner + the add action, so
				    the section explains itself instead of a bare text line. */}
				{methods.length === 0 && (
					<div className="text-center py-6">
						<FontAwesomeIcon
							icon={faCreditCard}
							className="text-3xl text-brand-muted mb-3"
						/>
						<p className="text-sm font-medium text-default">
							{t('No saved payment methods')}
						</p>
						<p className="text-xs text-brand-muted mt-1">
							{t('Add a card or bank account to make paying invoices faster.')}
						</p>
						<button
							type="button"
							onClick={() => {
								setShowAddSidebar(true);
							}}
							className="mt-3 px-4 py-2 text-sm font-medium rounded-lg border border-brand-border bg-brand-surface hover:border-portal-accent transition-colors min-h-touch"
						>
							{t('Add payment method')}
						</button>
					</div>
				)}

				{methods.length > 0 && (
					<div className="divide-y divide-subtle">
						{methods.map((method) => {
							const exp = formatExpiration(method);
							const isPrimary = method.mPaymentIsPrimary;
							const isDeleting = deletingId === method.id;
							const isSettingPrimary = settingPrimaryId === method.id;

							return (
								<div
									key={method.id}
									className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0"
								>
									{/* Primary star toggle */}
									<button
										type="button"
										onClick={() => {
											if (!isPrimary) {
												void handleSetPrimary(method.id);
											}
										}}
										disabled={isPrimary || isSettingPrimary}
										className="shrink-0 mr-2.5 p-1 disabled:cursor-default transition-colors"
										aria-label={
											isPrimary
												? t('Primary payment method')
												: t('Set as primary')
										}
									>
										{isSettingPrimary ? (
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-warning" />
										) : (
											<FontAwesomeIcon
												icon={isPrimary ? faStarSolid : faStarRegular}
												className={
													isPrimary
														? 'text-sm text-warning'
														: 'text-sm text-brand-muted hover:text-warning'
												}
											/>
										)}
									</button>
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2">
											{/* PD-15878 — the network's own mark. Customers recognise
											    a card by its logo well before four digits. */}
											<CardBrandIcon
												brand={method.mPaymentSubtype}
												className="text-lg text-brand-muted shrink-0"
												title={formatMethodLabel(method)}
											/>
											<span className="text-sm text-default truncate">
												{t(formatMethodLabel(method))}
											</span>
										</div>
										{exp != null && (
											<div className="text-xs text-brand-muted mt-0.5">
												{t('Exp')} {t(exp)}
											</div>
										)}
										{/* PD-17009 — said in words, not only by the icon's
										    color, so the state reads without it. */}
										{method.isSharedWithOwnership ? (
											<div className="text-xs text-brand-muted mt-0.5">
												{t('Shared with your ownership group')}
											</div>
										) : null}
									</div>
									{/* PD-17009 — share with every account in the ownership
									    group. Offered only when the account is in one. */}
									{canShareWithOwnership ? (
										<Tooltip
											content={
												method.isSharedWithOwnership
													? t('Stop sharing with your ownership group')
													: t('Share with every account in your ownership group')
											}
											delay={300}
										>
											<button
												type="button"
												onClick={() => {
													handleToggleSharing(method);
												}}
												disabled={sharingId === method.id}
												aria-pressed={method.isSharedWithOwnership}
												aria-label={
													method.isSharedWithOwnership
														? t('Stop sharing with your ownership group')
														: t(
																'Share with every account in your ownership group',
															)
												}
												className="shrink-0 ml-3 p-2 disabled:opacity-50 transition-colors"
											>
												{sharingId === method.id ? (
													<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-portal-accent" />
												) : (
													<FontAwesomeIcon
														icon={
															method.isSharedWithOwnership
																? faUsersSolid
																: faUsersRegular
														}
														className={
															method.isSharedWithOwnership
																? 'text-sm text-portal-accent'
																: 'text-sm text-brand-muted hover:text-portal-accent'
														}
													/>
												)}
											</button>
										</Tooltip>
									) : null}
									<button
										type="button"
										onClick={() => {
											// PD-17009 — deleting a SHARED method first says
											// what on other accounts will stop working. One
											// that is not shared is deleted as before.
											if (method.isSharedWithOwnership) {
												promptStopSharing(method, 'delete');
											} else {
												void handleDelete(method.id);
											}
										}}
										disabled={isDeleting}
										className="shrink-0 ml-3 p-2 text-brand-muted hover:text-danger disabled:opacity-50 transition-colors"
										aria-label={t('Delete payment method')}
									>
										{isDeleting ? (
											<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-danger" />
										) : (
											<FontAwesomeIcon icon={faTrashCan} className="text-sm" />
										)}
									</button>
								</div>
							);
						})}
					</div>
				)}
				{sharingError != null ? (
					<p role="alert" className="text-xs text-danger mt-3">
						{t(sharingError)}
					</p>
				) : null}
			</div>

			{/* PD-17009 — methods other accounts in the ownership group share
			    with this one. Read-only: no star, no share control, no delete,
			    because none of them is this account's to manage. */}
			{sharedWithAccount.length > 0 ? (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<h2 className="text-sm font-semibold text-default">
						{t('Shared with this account')}
					</h2>
					<p className="text-xs text-brand-muted mt-1">
						{t(
							'Other accounts in your ownership group share these payment methods. You can pay with them, and only the account that shared one can change or remove it.',
						)}
					</p>
					<div className="divide-y divide-subtle mt-3">
						{sharedWithAccount.map((method) => {
							const exp = formatExpiration(method);

							return (
								<div
									key={method.id}
									className="flex items-center gap-2 py-2.5 first:pt-0 last:pb-0"
								>
									<CardBrandIcon
										brand={method.mPaymentSubtype}
										className="text-lg text-brand-muted shrink-0"
										title={formatMethodLabel(method)}
									/>
									<div className="min-w-0 flex-1">
										<div className="text-sm text-default truncate">
											{t(formatMethodLabel(method))}
										</div>
										<div className="text-xs text-brand-muted mt-0.5">
											{formatPaymentMethodSharedByLabel(
												{
													relation: 'ownership',
													ownerClientName: method.ownerClientName,
												},
												t,
											)}
											{exp != null ? (
												<>
													{' · '}
													{t('Exp')} {t(exp)}
												</>
											) : null}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			) : null}

			{/* PD-17009 — un-sharing or deleting a shared method lists what on
			    other accounts will stop working, then lets it go ahead. */}
			<PaymentMethodStopSharingDialog
				isOpen={stopSharingPrompt != null}
				action={stopSharingPrompt?.action ?? 'stopSharing'}
				dependents={stopSharingPrompt?.dependents ?? null}
				isLoading={stopSharingPrompt?.dependents === undefined}
				onConfirm={() => {
					const prompt = stopSharingPrompt;
					setStopSharingPrompt(null);
					if (prompt == null) {
						return;
					}
					if (prompt.action === 'delete') {
						void handleDelete(prompt.method.id);
					} else {
						void saveSharing(prompt.method, false);
					}
				}}
				onCancel={() => {
					setStopSharingPrompt(null);
				}}
			/>

			{/* Add Payment Method sidebar — same pattern as Cart page */}
			<PortalSidebar
				isOpen={showAddSidebar}
				onClose={() => {
					setShowAddSidebar(false);
				}}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={handleAddSuccess}
						onCancel={() => {
							setShowAddSidebar(false);
						}}
						supportedPaymentMethods={supportedTypes}
					/>
				</div>
			</PortalSidebar>
		</>
	);
};
